/**
 * Centralized React-Query cache keys.
 *
 * Keeping every consumer of an Allergy/Medication/Vital/Condition/Note/etc.
 * on the same key shape means an invalidate from any place refreshes the
 * data in every place. Avoids the bug where reconciling allergies inside
 * the visit cockpit's RoomingChecklistPanel left the cockpit nav-rail
 * counter stale.
 *
 * Convention:
 *   - patient-level data: `['patient', patientId, ...]`
 *   - encounter-level data (today's diagnoses, today's notes, AVS): `['encounter', encounterId, ...]`
 *   - appointment-level data: `['appointment', appointmentId, ...]`
 */

export const cacheKeys = {
  // ── Patient-level (longitudinal) ─────────────────────────────────
  patientAllergies: (patientId: string, opts?: { activeOnly?: boolean }) =>
    ['patient', patientId, 'allergies', opts?.activeOnly ? 'active' : 'all'] as const,

  patientMedications: (patientId: string, opts?: { activeOnly?: boolean }) =>
    ['patient', patientId, 'medications', opts?.activeOnly ? 'active' : 'all'] as const,

  patientConditions: (patientId: string, opts?: { activeOnly?: boolean }) =>
    ['patient', patientId, 'conditions', opts?.activeOnly ? 'active' : 'all'] as const,

  patientImmunizations: (patientId: string) =>
    ['patient', patientId, 'immunizations'] as const,

  patientVitalsLatest: (patientId: string) =>
    ['patient', patientId, 'vitals', 'latest'] as const,

  patientVitals: (patientId: string) =>
    ['patient', patientId, 'vitals', 'all'] as const,

  patientVitalTrend: (patientId: string, loincCode: string) =>
    ['patient', patientId, 'vitals', 'trend', loincCode] as const,

  patientNotes: (patientId: string, page?: number) =>
    ['patient', patientId, 'notes', page ?? 1] as const,

  patientPrescriptions: (patientId: string) =>
    ['patient', patientId, 'prescriptions'] as const,

  patientLabOrders: (patientId: string) =>
    ['patient', patientId, 'lab-orders'] as const,

  patientLabResults: (patientId: string) =>
    ['patient', patientId, 'lab-results'] as const,

  patientAppointments: (patientId: string) =>
    ['patient', patientId, 'appointments'] as const,

  // ── Encounter-level (visit-scoped) ───────────────────────────────
  encounter: (encounterId: string) =>
    ['encounter', encounterId] as const,

  encounterVitals: (encounterId: string) =>
    ['encounter', encounterId, 'vitals'] as const,

  encounterAvs: (encounterId: string) =>
    ['encounter', encounterId, 'avs'] as const,

  encounterLabOrders: (encounterId: string) =>
    ['encounter', encounterId, 'lab-orders'] as const,

  patientActiveEncounter: (patientId: string) =>
    ['patient', patientId, 'active-encounter'] as const,

  // ── Appointment-level ────────────────────────────────────────────
  appointment: (appointmentId: string) =>
    ['appointment', appointmentId] as const,

  appointmentIntakes: (appointmentId: string) =>
    ['appointment', appointmentId, 'intakes'] as const,

  appointmentLabOrders: (appointmentId: string) =>
    ['appointment', appointmentId, 'lab-orders'] as const,
};

/**
 * For invalidations that should refresh all variants of a slice
 * (e.g., active + all). Use as a queryKey predicate-prefix.
 */
export const cacheRoots = {
  patientAllergies: (patientId: string) => ['patient', patientId, 'allergies'] as const,
  patientMedications: (patientId: string) => ['patient', patientId, 'medications'] as const,
  patientConditions: (patientId: string) => ['patient', patientId, 'conditions'] as const,
  patientVitals: (patientId: string) => ['patient', patientId, 'vitals'] as const,
};
